import { useCallback, useEffect, useMemo, useState } from "react";
import api, { extractErr } from "../lib/api";
import { toast } from "sonner";
import { RefreshCw, RotateCcw, CheckCircle2, XCircle, Clock, Calendar, PauseCircle } from "lucide-react";

const STATUS_TABS = [
  { key: "all", label: "All", sub: "全部", filter: null },
  { key: "success", label: "Success", sub: "成功", filter: "success" },
  { key: "failed", label: "Failed", sub: "失败", filter: "failed" },
  { key: "pending", label: "Pending", sub: "待发送", filter: "pending" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("zh-CN", { hour12: false, month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function MessagesDashboard() {
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pausedJobs, setPausedJobs] = useState([]);
  const [scheduledJobs, setScheduledJobs] = useState([]);
  const [retrying, setRetrying] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, jobs, msgsResp] = await Promise.all([
        api.get("/dashboard/message-stats"),
        api.get("/whatsapp/bulk-jobs?limit=50"),
        api.get(`/dashboard/messages?limit=50${STATUS_TABS.find(t => t.key === tab)?.filter ? `&status=${STATUS_TABS.find(t => t.key === tab).filter}` : ""}`),
      ]);
      setStats(s.data);
      setMessages(msgsResp.data.messages || []);
      setTotal(msgsResp.data.total || 0);
      const allJobs = jobs.data.jobs || [];
      setPausedJobs(allJobs.filter(j => j.status === "paused"));
      setScheduledJobs(allJobs.filter(j => j.status === "scheduled"));
    } catch (e) { toast.error(extractErr(e)); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const iv = setInterval(refresh, 15000);
    return () => clearInterval(iv);
  }, [refresh]);

  const retryOne = async (msgId) => {
    setRetrying(msgId);
    try {
      await api.post(`/messages/${msgId}/retry`);
      toast.success("已重新发送");
      refresh();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setRetrying(null); }
  };

  const retryJob = async (jobId) => {
    if (!confirm("为该任务中所有失败联系人重新发送？")) return;
    try {
      const { data } = await api.post(`/whatsapp/bulk-jobs/${jobId}/retry-failed`);
      toast.success(`已发起重试任务 · ${data.total} 条`);
      refresh();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const cancelScheduled = async (jobId) => {
    if (!confirm("取消该定时任务？")) return;
    try {
      await api.post(`/whatsapp/bulk-jobs/${jobId}/cancel`);
      toast.success("已取消");
      refresh();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const resumePaused = async (jobId) => {
    try {
      await api.post(`/whatsapp/bulk-jobs/${jobId}/resume`);
      toast.success("已恢复");
      refresh();
    } catch (e) { toast.error(extractErr(e)); }
  };

  const successRate = useMemo(() => {
    if (!stats?.today?.total) return 0;
    return Math.round((stats.today.success / stats.today.total) * 100);
  }, [stats]);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="messages-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-700 font-semibold">Message Log</div>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight">MESSAGE LOG <span className="text-lg font-normal text-slate-500">消息记录</span></h1>
        </div>
        <button data-testid="dashboard-refresh" onClick={refresh} disabled={loading} className="px-3 py-1.5 border border-slate-300 text-xs font-semibold rounded-sm hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh <span className="text-slate-400 font-normal">刷新</span>
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="p-4 bg-white border border-slate-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Total Today <span className="text-slate-400 normal-case tracking-normal font-normal">今日总数</span></div>
          <div className="text-3xl font-display font-black" data-testid="stats-total">{stats?.today?.total ?? 0}</div>
        </div>
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1">Success <span className="text-emerald-600 normal-case tracking-normal font-normal">成功</span></div>
          <div className="text-3xl font-display font-black text-emerald-800" data-testid="stats-success">{stats?.today?.success ?? 0}</div>
          <div className="text-[10px] text-emerald-700 mt-0.5">{successRate}% success rate 成功率</div>
        </div>
        <div className="p-4 bg-red-50 border border-red-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-semibold mb-1">Failed <span className="text-red-600 normal-case tracking-normal font-normal">失败</span></div>
          <div className="text-3xl font-display font-black text-red-800" data-testid="stats-failed">{stats?.today?.failed ?? 0}</div>
        </div>
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold mb-1">Pending <span className="text-amber-600 normal-case tracking-normal font-normal">待发送</span></div>
          <div className="text-3xl font-display font-black text-amber-800" data-testid="stats-pending">{stats?.today?.pending ?? 0}</div>
        </div>
        <div className="p-4 bg-slate-100 border border-slate-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-semibold mb-1">Daily Quota <span className="text-slate-500 normal-case tracking-normal font-normal">每日额度</span></div>
          <div className="text-3xl font-display font-black text-slate-800">{stats?.daily_sent_today ?? 0}<span className="text-sm font-normal text-slate-500"> / {stats?.daily_cap ?? 1000}</span></div>
        </div>
      </div>

      {/* Paused jobs banner */}
      {pausedJobs.length > 0 && (
        <div className="p-4 bg-red-50 border-2 border-red-400 rounded-sm space-y-2" data-testid="paused-jobs">
          <div className="flex items-center gap-2 text-red-900 font-bold text-sm">
            <PauseCircle size={16} /> {pausedJobs.length} Paused Jobs Need Attention <span className="text-red-700 font-normal">· 个任务已暂停</span>
          </div>
          {pausedJobs.slice(0, 3).map(j => (
            <div key={j.id} className="flex items-center justify-between text-xs bg-white/60 p-2 rounded-sm">
              <div className="min-w-0 flex-1 mr-2">
                <div className="font-semibold truncate">{j.mode === "wati_template" ? `📋 ${j.wati_template_name}` : "Freeform 自由文本"} · {j.sent}/{j.total}</div>
                <div className="text-red-700 truncate">{j.pause_reason}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => resumePaused(j.id)} className="px-2 py-1 bg-emerald-700 text-white rounded-sm hover:bg-emerald-800 text-[10px] font-semibold">Resume 继续</button>
                <button onClick={() => retryJob(j.id)} className="px-2 py-1 border border-slate-300 rounded-sm hover:bg-slate-50 text-[10px] font-semibold">Retry Failed 重试失败</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Scheduled jobs */}
      {scheduledJobs.length > 0 && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-sm space-y-2" data-testid="scheduled-jobs">
          <div className="flex items-center gap-2 text-blue-900 font-bold text-sm">
            <Calendar size={16} /> {scheduledJobs.length} Scheduled Jobs <span className="text-blue-700 font-normal">· 个排程任务</span>
          </div>
          {scheduledJobs.slice(0, 5).map(j => (
            <div key={j.id} className="flex items-center justify-between text-xs bg-white p-2 rounded-sm">
              <div className="min-w-0 flex-1 mr-2">
                <div className="font-semibold truncate">{j.mode === "wati_template" ? `📋 ${j.wati_template_name}` : "Freeform 自由文本"} · {j.total}</div>
                <div className="text-blue-700">⏰ {fmtTime(j.scheduled_for)}</div>
              </div>
              <button onClick={() => cancelScheduled(j.id)} className="px-2 py-1 border border-red-300 text-red-700 rounded-sm hover:bg-red-50 text-[10px] font-semibold">Cancel 取消</button>
            </div>
          ))}
        </div>
      )}

      {/* Message list */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {STATUS_TABS.map(t => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-xs font-semibold border-b-2 whitespace-nowrap ${tab === t.key ? "border-emerald-700 text-emerald-800" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              {t.label} <span className="text-slate-400 font-normal">{t.sub}</span>
              {t.key === "all" && <span className="ml-1 text-[10px] text-slate-400">({total})</span>}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="p-2 text-left">Contact <span className="text-slate-400 normal-case tracking-normal font-normal">联系人</span></th>
                <th className="p-2 text-left">Type <span className="text-slate-400 normal-case tracking-normal font-normal">类型</span></th>
                <th className="p-2 text-left">Message <span className="text-slate-400 normal-case tracking-normal font-normal">内容</span></th>
                <th className="p-2 text-left">Time <span className="text-slate-400 normal-case tracking-normal font-normal">时间</span></th>
                <th className="p-2 text-left">Status <span className="text-slate-400 normal-case tracking-normal font-normal">状态</span></th>
                <th className="p-2 text-left">Action <span className="text-slate-400 normal-case tracking-normal font-normal">操作</span></th>
              </tr>
            </thead>
            <tbody data-testid="messages-table">
              {messages.length === 0 && !loading && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">No messages · 无消息</td></tr>
              )}
              {messages.map(m => {
                const st = (m.status || "").toLowerCase();
                const isSuccess = ["sent", "accepted", "delivered", "read"].includes(st);
                const isFailed = st === "failed";
                // WhatsApp-style status pill
                const statusPill = (() => {
                  if (st === "read") return <span className="inline-flex items-center gap-0.5 text-blue-700 font-semibold" title="Read · 已读"><CheckCircle2 size={11} className="-mr-1.5"/><CheckCircle2 size={11} /> Read</span>;
                  if (st === "delivered") return <span className="inline-flex items-center gap-0.5 text-slate-700 font-semibold" title="Delivered · 已送达"><CheckCircle2 size={11} className="-mr-1.5"/><CheckCircle2 size={11} /> Delivered</span>;
                  if (st === "sent" || st === "accepted") return <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" title="Sent · 已发送"><CheckCircle2 size={11} /> Sent</span>;
                  if (st === "failed") return <span className="inline-flex items-center gap-1 text-red-700 font-semibold" title={m.error || "Failed"}><XCircle size={11} /> Failed</span>;
                  if (st === "retried") return <span className="inline-flex items-center gap-1 text-slate-500" title="重发过">↻ Retried</span>;
                  return <span className="inline-flex items-center gap-1 text-amber-700"><Clock size={11} /> {st || "pending"}</span>;
                })();
                return (
                  <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="p-2">
                      <div className="font-semibold">{m.contact_name || m.phone || "—"}</div>
                      <div className="text-[10px] text-slate-500">{m.phone}</div>
                    </td>
                    <td className="p-2">
                      {m.kind === "wati_template" ? (
                        <span className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-sm text-[10px] font-semibold">📋 {m.template_name}</span>
                      ) : m.media_path ? (
                        <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-sm text-[10px] font-semibold">📎 image</span>
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded-sm text-[10px] font-semibold">💬 text</span>
                      )}
                    </td>
                    <td className="p-2 max-w-[300px]"><div className="truncate">{m.text}</div></td>
                    <td className="p-2 text-slate-500 whitespace-nowrap">{fmtTime(m.timestamp)}</td>
                    <td className="p-2" data-testid={`msg-status-${m.id}`}>{statusPill}</td>
                    <td className="p-2">
                      {isFailed && (
                        <button
                          data-testid={`retry-${m.id}`}
                          onClick={() => retryOne(m.id)}
                          disabled={retrying === m.id}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border border-emerald-300 text-emerald-700 rounded-sm hover:bg-emerald-50 disabled:opacity-40 font-semibold"
                        >
                          <RotateCcw size={11} /> {retrying === m.id ? "…" : "Retry 重试"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
